import React, { useState, useMemo } from 'react';
import StandaloneSection from './StandaloneSection';
import InsightsTabs from './InsightsTabs';
import InsightCard from './InsightCard';
import { InsightType } from '../../types/insights';
import { InsightGenerationData, generateInsights, filterInsightsByType } from '../../utils/insightOrchestration';
import './IntelligentInsights.css';

interface IntelligentInsightsProps {
  data: InsightGenerationData;
  isLoading?: boolean;
  error?: string | null;
}

const IntelligentInsights: React.FC<IntelligentInsightsProps> = ({
  data,
  isLoading = false,
  error = null,
}) => {
  const [activeTab, setActiveTab] = useState<InsightType | 'all'>('all');

  // Generate insights from data
  const insightsAnalysis = useMemo(() => {
    if (!data.dailyData || data.dailyData.length < 7) {
      return null;
    }

    try {
      return generateInsights(data);
    } catch (err) {
      console.error('Failed to generate insights:', err);
      return null;
    }
  }, [data]);

  // Filter insights based on active tab
  const filteredInsights = useMemo(() => {
    if (!insightsAnalysis) {
      return [];
    }

    return filterInsightsByType(insightsAnalysis.insights, activeTab);
  }, [insightsAnalysis, activeTab]);

  // Loading state
  if (isLoading) {
    return (
        <StandaloneSection title="INTELLIGENT INSIGHTS">
          <div className="insights-loading">
            <div className="loading-spinner"></div>
            <p>Analyzing trading patterns...</p>
          </div>
        </StandaloneSection>
    );
  }

  // Error state
  if (error) {
    return (
        <StandaloneSection title="INTELLIGENT INSIGHTS">
          <div className="insights-error">
            <p className="error-icon" aria-hidden="true">⚠️</p>
            <p className="error-message">{error}</p>
          </div>
        </StandaloneSection>
    );
  }

  // Insufficient data state
  if (!data.dailyData || data.dailyData.length < 7) {
    return (
        <StandaloneSection title="INTELLIGENT INSIGHTS">
          <div className="insights-empty">
            <p className="empty-icon" aria-hidden="true">📊</p>
            <h3 className="empty-title">Not Enough Data</h3>
            <p className="empty-message">
              Insights require at least 7 days of trading data to identify meaningful patterns.
              Continue trading and check back once you have more historical data.
            </p>
          </div>
        </StandaloneSection>
    );
  }

  // No insights generated
  if (!insightsAnalysis || insightsAnalysis.insights.length === 0) {
    return (
        <StandaloneSection title="INTELLIGENT INSIGHTS">
          <div className="insights-empty">
            <p className="empty-icon" aria-hidden="true">✅</p>
            <h3 className="empty-title">No Significant Patterns Detected</h3>
            <p className="empty-message">
              Your trading performance is consistent without notable patterns or anomalies.
              This analysis updates automatically as more data becomes available.
            </p>
          </div>
        </StandaloneSection>
    );
  }

  // No insights for selected filter
  if (filteredInsights.length === 0 && activeTab !== 'all') {
    return (
        <StandaloneSection title="INTELLIGENT INSIGHTS">
          <InsightsTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            opportunityCount={insightsAnalysis.summary.opportunityCount}
            warningCount={insightsAnalysis.summary.warningCount}
            patternCount={insightsAnalysis.summary.patternCount}
            totalCount={insightsAnalysis.summary.totalInsights}
          />
          <div className="insights-empty">
            <p className="empty-icon" aria-hidden="true">🔍</p>
            <h3 className="empty-title">No {activeTab}s Found</h3>
            <p className="empty-message">
              No {activeTab} insights detected in the current period. Try viewing all insights or selecting a different category.
            </p>
          </div>
        </StandaloneSection>
    );
  }

  // Normal state with insights
  return (
      <StandaloneSection title="INTELLIGENT INSIGHTS">
        <div className="insights-intro">
          <p>
            Automatically detected patterns and opportunities from your trading data.
            Insights are prioritized by potential impact to help you optimize your strategy.
          </p>
        </div>

        <InsightsTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          opportunityCount={insightsAnalysis.summary.opportunityCount}
          warningCount={insightsAnalysis.summary.warningCount}
          patternCount={insightsAnalysis.summary.patternCount}
          totalCount={insightsAnalysis.summary.totalInsights}
        />

        <div 
          className="insights-grid"
          role="tabpanel"
          id={`insights-panel-${activeTab}`}
          aria-label={`${activeTab === 'all' ? 'All' : activeTab} insights`}
        >
          {filteredInsights.map(insight => (
            <InsightCard key={insight.id} insight={insight} />
          ))}
        </div>

        <div className="insights-footer">
          <p className="insights-disclaimer">
            💡 Insights are based on historical patterns and statistical analysis. Past performance does not guarantee future results.
          </p>
        </div>
      </StandaloneSection>
  );
};

export default IntelligentInsights;
